import { Delete } from "@mui/icons-material";
import { Button, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import moment from "moment";
import { useSnackbar } from "notistack";
import { Costing } from "pdf-generator";
import { useMemo } from "react";
import { DeletePanel, MyPaper } from "../../../components";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useDeleteCostingMutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog, updateConfirmationAction } from "../../../redux/slices/confirmationDialog";
import { closeDialog } from "../../../redux/slices/dialog";

export const ListingAvailableCosts = ({ readOnly, costings, onSelect, refetchCosting }: { readOnly: boolean, costings: Costing[], onSelect: (costing: Costing) => void, refetchCosting: () => void }) => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const { pricing } = useAppSelector(store => store);

    const [deleteCosting] = useDeleteCostingMutation();

    const reversed = useMemo(() => {
        return [...costings].sort((a, b) => new Date(b.created_time).getTime() - new Date(a.created_time).getTime());
    }, [costings]);

    const hasLatest = useMemo(() => {
        const latestCosting = (costings ?? [])?.filter((d: any) => new Date(d.created_time) > new Date(pricing.modified_time));
        return latestCosting.length > 0;
    }, [costings, pricing?.modified_time])

    const initiateDeleteCosting = (props: { costing_id?: string, pricing_id?: string }) => {
        dispatch(
          openConfirmationDialog({
            title: "Delete Operation",
            body: `Are you sure you want to delete ${props.pricing_id ? "all the costings?" : " the costing?"}`,
            positiveBtn: `${props.pricing_id ? "Delete All" : "Delete"}`,
            negativeBtn: "Cancel",
            onOk: () => {
                handleDeleteCosting(props);
                dispatch(closeConfirmationDialog());
            },
            onNegativeBtn: () => dispatch(closeConfirmationDialog()),
          })
        );
    };

    const handleDeleteCosting = async ({ costing_id, pricing_id }: { costing_id?: string, pricing_id?: string }) => {
        dispatch(openBackdrop(`Deleting costing details ...`));
        try {
            await deleteCosting({ costing_id: costing_id, pricing_id: pricing_id }).unwrap().then((res) => {
                refetchCosting();
                dispatch(closeDialog());
            })
        } catch (error: any) {
            let errorMessage: string = error?.data?.title ?? `Unable to delete costing details!`;
            enqueueSnackbar(errorMessage, { variant: "error" });
            dispatch(openConfirmationDialog({ title: <Typography variant="h6">Unable to Delete the Costing Details</Typography>, body: <DeletePanel message={"Dependency table of " + pricing.sequenceId} errorMessage={errorMessage} errorMessageStacks={[{ object_type: "tag", object_details: (error?.data?.details?.referenced_quotes ?? []).map((e: any) => e.sequence_id) }]} /> }));
            dispatch(updateConfirmationAction({
                hideForceBtn: true,
                hideNegativeBtn: true,
                hidePositiveBtn: true
            }))
            dispatch(closeDialog());
        } finally {
            dispatch(closeBackdrop());
        }
    }

    return (
        <>
            <DialogTitle>
                <Stack
                    alignItems={"center"}
                    direction={"row"}
                    justifyContent={"space-between"}
                    sx={{
                        pb: 1,
                        width: "100%",
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Typography
                        variant="h6"
                        color="textPrimary"
                        sx={{
                            fontFamily: 'htrts_medium',
                        }}
                    >
                        Calculated Costs
                    </Typography>
                    {!readOnly &&
                        <Button
                            sx={{ mr: 4 }}
                            variant="outlined"
                            color="error"
                            onClick={() => initiateDeleteCosting({ pricing_id: pricing?.id })}
                        >
                            Delete All
                        </Button>
                    }

                </Stack>

            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {reversed.map((costing, idx: number) => {
                        return (
                            <MyPaper
                                padding={0}
                            >
                                <Stack gap={1} sx={{ justifyContent: "space-between" }} direction={"row"}>
                                    <Stack
                                        sx={{
                                            ':hover': {
                                                bgcolor: grey[100]
                                            },
                                            cursor: "pointer",
                                            p: 1.5
                                        }}
                                        onClick={() => onSelect(costing)}
                                    >
                                        <Typography fontWeight={"bold"}>Costing {idx + 1} {(idx === 0 && hasLatest) ? <Typography variant="caption" color="primary" fontWeight={"bold"}>(Latest)</Typography> : ''}</Typography>
                                        <span><Typography variant={"caption"}>{moment(new Date(costing.created_time), 'MM-DD-YYYY HH:mm:ss', true).format("YYYY-MM-DD HH:mm:ss")}</Typography> | <Typography variant="caption">{costing.created_user}</Typography></span>
                                    </Stack>

                                    {!readOnly &&
                                        <IconButton
                                            edge="start"
                                            color="inherit"
                                            onClick={() => initiateDeleteCosting({ costing_id: costing?._id })}
                                            aria-label="delete"
                                            disableRipple
                                        >
                                            <Delete
                                                color="error"
                                            />
                                        </IconButton>
                                    }

                                </Stack>
                            </MyPaper>
                        )
                    })}
                </Stack>
            </DialogContent>
        </>
    );
};